import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ColorRow } from './ColorRow';
import { ResetButton } from './ResetButton';
import { SectionHeader } from './SectionHeader';
import { WCAG_AA_NORMAL, getContrastRatio } from './contrast';
import { DEFAULT_WIDGET_THEME_CUSTOM } from './themePresets';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import {
  WidgetThemeColorsDto,
  WidgetThemeFontSize,
  WidgetThemeMode,
  WidgetThemeRadiusSize,
} from '@/Domain/api/WIdget/dto/WidgetTheme.dto';
import { ColorPickerDropdown } from '@/Domain/components/ColorPickerDropdown';
import { FormControl, FormField, FormItem, FormLabel } from '@/Domain/components/Shared/Form/Form';
import {
  RadioGroupSegmented,
  RadioGroupSegmentedItem,
} from '@/Domain/components/Shared/RadioGroup/RadioGroup';

const COLOR_FIELDS: (keyof WidgetThemeColorsDto)[] = [
  'background',
  'header',
  'header_text',
  'accent',
  'accent_hover',
  'accent_text',
  'bubble',
  'bubble_text',
  'ai_bubble_text',
  'tertiary',
  'link',
  'input_background',
  'input_text',
  'input_placeholder',
  'border',
];

// Foreground+background pairs checked for WCAG AA contrast. Warning is
// rendered on the foreground field so we don't double-warn on both sides of
// the pair. accent_hover is intentionally absent (no paired text).
const CONTRAST_PAIRS: Partial<Record<keyof WidgetThemeColorsDto, keyof WidgetThemeColorsDto>> = {
  header_text: 'header',
  accent_text: 'accent',
  bubble_text: 'bubble',
  ai_bubble_text: 'background',
  tertiary: 'background',
  link: 'background',
  input_text: 'input_background',
  input_placeholder: 'input_background',
};

const RADIUS_SIZES: WidgetThemeRadiusSize[] = ['sm', 'base', 'lg', 'full'];

const FONT_SIZES: WidgetThemeFontSize[] = ['sm', 'base', 'lg'];

const MODES: WidgetThemeMode[] = ['light', 'dark', 'custom'];

const CustomColorFields = () => {
  const { t } = useTranslation('widget');
  const colors = useWatch<WidgetSettingsDto>({
    name: 'widget_theme_custom.colors',
  }) as WidgetThemeColorsDto | undefined;

  return (
    <div className="flex flex-col gap-6" data-testid="theme-custom-colors">
      {COLOR_FIELDS.map((colorKey) => {
        const pairKey = CONTRAST_PAIRS[colorKey];
        const fg = colors?.[colorKey];
        const bg = pairKey ? colors?.[pairKey] : null;
        let warning: string | undefined;
        if (fg && bg) {
          const ratio = getContrastRatio(fg, bg);
          if (ratio !== null && ratio < WCAG_AA_NORMAL) {
            warning = t('theme.colors.contrast_warning', { ratio: ratio.toFixed(2) });
          }
        }

        return (
          <FormField
            key={colorKey}
            name={`widget_theme_custom.colors.${colorKey}`}
            render={({ field }) => (
              <ColorRow
                label={t(`theme.colors.${colorKey}_label`)}
                hint={t(`theme.colors.${colorKey}_hint`)}
                warning={warning}
                testId={`field-theme-color-${colorKey}`}
                onReset={() => field.onChange(null)}
                control={
                  <ColorPickerDropdown
                    color={field.value ?? ''}
                    onColorChange={(color) => field.onChange(color || null)}
                    pickerId={`theme-color-${colorKey}`}
                    allowGradient={false}
                  />
                }
              />
            )}
          />
        );
      })}
    </div>
  );
};

export const ThemeSection = () => {
  const { t } = useTranslation('widget');
  const form = useFormContext<WidgetSettingsDto>();
  const mode = useWatch({ control: form.control, name: 'theme' });

  return (
    <div className="flex flex-col gap-6" data-testid="theme-section">
      <SectionHeader label={t('theme.section_title')} testId="section-theme" />

      <FormField
        name="theme"
        control={form.control}
        render={({ field }) => (
          <FormItem data-testid="field-theme-mode">
            <FormLabel>{t('theme.mode.label')}</FormLabel>
            <FormControl>
              <RadioGroupSegmented value={field.value ?? 'light'} onValueChange={field.onChange}>
                {MODES.map((m) => (
                  <RadioGroupSegmentedItem
                    key={m}
                    value={m}
                    id={`theme-mode-${m}`}
                    data-testid={`theme-mode-${m}`}
                  >
                    {t(`theme.mode.${m}`)}
                  </RadioGroupSegmentedItem>
                ))}
              </RadioGroupSegmented>
            </FormControl>
          </FormItem>
        )}
      />

      {/* Colors (only when mode === 'custom') */}
      {mode === 'custom' && (
        <>
          <SectionHeader label={t('theme.colors.section_title')} testId="section-theme-colors" />
          <CustomColorFields />
        </>
      )}

      {/* Radius (all modes). Single control writes the same value to all three
          schema keys (bubble/input/button); `bubble` is the read source. */}
      <SectionHeader label={t('theme.radius.section_title')} testId="section-theme-radius" />
      <FormField
        name="widget_theme_custom.radius.bubble"
        render={({ field }) => {
          const defaultValue = DEFAULT_WIDGET_THEME_CUSTOM.radius.bubble ?? 'lg';
          const value: WidgetThemeRadiusSize = field.value ?? defaultValue;
          const setAll = (v: WidgetThemeRadiusSize) => {
            form.setValue('widget_theme_custom.radius.bubble', v, { shouldDirty: true });
            form.setValue('widget_theme_custom.radius.input', v, { shouldDirty: true });
            form.setValue('widget_theme_custom.radius.button', v, { shouldDirty: true });
          };
          return (
            <FormItem
              data-testid="field-theme-radius"
              className="grid grid-cols-[1fr_auto] items-center gap-4 space-y-0"
            >
              <FormLabel className="text-sm">{t('theme.radius.size_label')}</FormLabel>
              <div className="flex items-center gap-2">
                <FormControl>
                  <RadioGroupSegmented value={value} onValueChange={setAll}>
                    {RADIUS_SIZES.map((size) => (
                      <RadioGroupSegmentedItem
                        key={size}
                        value={size}
                        id={`theme-radius-${size}`}
                        data-testid={`theme-radius-${size}`}
                      >
                        {t(`theme.radius.sizes.${size}`)}
                      </RadioGroupSegmentedItem>
                    ))}
                  </RadioGroupSegmented>
                </FormControl>
                <ResetButton
                  onClick={() => setAll(defaultValue)}
                  testId="field-theme-radius-reset"
                />
              </div>
            </FormItem>
          );
        }}
      />

      {/* Font (all modes) */}
      <SectionHeader label={t('theme.font.section_title')} testId="section-theme-font" />
      <FormField
        name="widget_theme_custom.font.size"
        render={({ field }) => (
          <FormItem
            data-testid="field-theme-font-size"
            className="grid grid-cols-[1fr_auto] items-center gap-4 space-y-0"
          >
            <FormLabel className="text-sm">{t('theme.font.size_label')}</FormLabel>
            <div className="flex items-center gap-2">
              <FormControl>
                <RadioGroupSegmented value={field.value ?? 'base'} onValueChange={field.onChange}>
                  {FONT_SIZES.map((size) => (
                    <RadioGroupSegmentedItem
                      key={size}
                      value={size}
                      id={`theme-font-size-${size}`}
                      data-testid={`theme-font-size-${size}`}
                    >
                      {t(`theme.font.sizes.${size}`)}
                    </RadioGroupSegmentedItem>
                  ))}
                </RadioGroupSegmented>
              </FormControl>
              <ResetButton
                onClick={() => field.onChange(DEFAULT_WIDGET_THEME_CUSTOM.font.size)}
                testId="field-theme-font-size-reset"
              />
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};
